import useOnClickOutside from '@hooks/useOnClickOutside';
import { createClsNameMap, mapModifiers } from '@utils';
import { useRef, useState } from 'react';

export const clsNameMap = createClsNameMap({
  elements: {
    'selected-option': createClsNameMap(),
    'option-list': createClsNameMap({
      modifiers: ['open'],
    }),
    option: createClsNameMap({
      modifiers: ['selected'],
    }),
    'option-text': createClsNameMap(),
  },
  modifiers: ['disabled'],
})('dropdown');

export type DropdownOption = {
  label: string;
  value: string;
};

const dropdownIcon = (
  <svg width='8' height='12' viewBox='0 0 8 5' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M7.09832 3.98009e-07L0.901685 -1.43717e-07C0.15069 -2.09371e-07 -0.269991 0.75351 0.193991 1.2676L3.29231 4.70055C3.65265 5.09982 4.34735 5.09982 4.70769 4.70055L7.80601 1.2676C8.26999 0.753511 7.84931 4.63664e-07 7.09832 3.98009e-07Z'
      fill='#5C5F62'
    />
  </svg>
);

export type DropdownProps = {
  options: Array<DropdownOption>;
  onSelect?: (selectedValue: string) => void;
  disabled?: boolean;
  showWhenNoOption?: boolean;
  defaultOption?: DropdownOption;
};

const Dropdown = ({
  options,
  onSelect,
  disabled,
  showWhenNoOption = true,
  defaultOption,
}: DropdownProps) => {
  const [selectedOption, setSelectedOption] = useState<DropdownOption>(defaultOption || options[0]);
  const [isShowOptionList, setIsShowOptionList] = useState<boolean>(false);

  const optionListRef = useRef<HTMLUListElement | null>(null);

  const handleOptionClick = (option: DropdownOption) => {
    setSelectedOption(option);
    if (onSelect) onSelect(option.value);
    setIsShowOptionList(false);
  };

  useOnClickOutside(optionListRef, () => {
    setIsShowOptionList(false);
  });

  if (options.length === 0 && !showWhenNoOption) {
    return <></>;
  }

  return (
    <div className={mapModifiers(clsNameMap, { disabled })}>
      <div className={clsNameMap.elm('selected-option')} onClick={() => setIsShowOptionList(true)}>
        <span
          className={clsNameMap.elm('option-text')}
          title={selectedOption?.label || 'No option to select'}
        >
          {selectedOption?.label || 'No option to select'}
        </span>
        {dropdownIcon}
      </div>
      {options.length > 0 && (
        <ul
          className={mapModifiers(clsNameMap['option-list'], {
            open: isShowOptionList,
          })}
          ref={optionListRef}
        >
          {options.map((option) => (
            <li
              key={option.value}
              className={mapModifiers(clsNameMap.option, {
                selected: selectedOption.value === option.value,
              })}
              onClick={() => handleOptionClick(option)}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Dropdown;
